import type { OrderForBarcode } from '@/models';

/**
 * AB#20737 (Zendesk #8681) — Rajco piece labels.
 *
 * 52 × 35 mm, printed at 203 dpi (TSC TE200 / Zebra ZT411). The barcode is a
 * 10-digit Code 128C payload: last 6 digits of the order number + 4-digit piece
 * index. The backend (`order/{no}/for/barcode`) owns the numbering so every
 * printer — admin v2 or Rajco Label Desk — produces the same barcode for a piece.
 */

export type BarcodeRow = OrderForBarcode['barcode_data'][number];

export interface PieceLabel {
  key: string;
  orderNo: string;
  barcode: string;
  pieceIndex: number;
  pieceTotal: number;
  designLabel: string;
  productName: string;
  playerName: string;
  playerNumber: string;
  size: string;
  isPair: boolean;
}

export const PIECE_LABEL_WIDTH_MM = 52;
export const PIECE_LABEL_HEIGHT_MM = 35;

/** 203 dpi → 3 dots per module ≈ 0.375 mm; Code 128C with 10 digits is 90 modules wide. */
export const PIECE_BARCODE_MODULE_MM = 0.375;
export const PIECE_BARCODE_MODULES = 90;

const PIECE_BARCODE_PATTERN = /^\d{10}$/;

export const isPieceBarcode = (value: string | undefined | null): boolean =>
  !!value && PIECE_BARCODE_PATTERN.test(value);

/** Decode a scanned piece barcode into its order part and 1-based piece index. */
export const decodePieceBarcode = (
  value: string,
): { orderPart: string; pieceIndex: number } | null => {
  const trimmed = value.trim();
  if (!isPieceBarcode(trimmed)) return null;
  const pieceIndex = Number(trimmed.slice(6));
  return pieceIndex >= 1 ? { orderPart: trimmed.slice(0, 6), pieceIndex } : null;
};

/** True when the backend has sent piece numbering for at least one printable row. */
export const hasPieceData = (rows: BarcodeRow[]): boolean =>
  rows.some((row) => (row.piece_barcodes?.length ?? 0) > 0);

/**
 * One label per piece. `quantity` on a row can only lower how many of that row's
 * pieces are printed (reprinting a few), never invent new piece numbers, so the
 * first `quantity` pieces of the row's range are used.
 */
export const buildPieceLabels = (
  rows: BarcodeRow[],
  orderNo: string,
  pieceTotal: number,
): PieceLabel[] => {
  const labels: PieceLabel[] = [];
  for (const row of rows) {
    const barcodes = (row.piece_barcodes ?? []).filter(isPieceBarcode);
    const from = row.piece_index_from ?? 0;
    if (!barcodes.length || from < 1) continue;
    const wanted = Math.min(barcodes.length, Math.max(0, Math.floor(Number(row.quantity) || 0)));
    for (let i = 0; i < wanted; i++) {
      labels.push({
        key: barcodes[i],
        orderNo,
        barcode: barcodes[i],
        pieceIndex: from + i,
        pieceTotal: pieceTotal || row.piece_index_to || from + i,
        designLabel: row.design_label ?? '',
        productName: row.product_name ?? '',
        playerName: row.player_name ?? '',
        playerNumber: row.player_number === null || row.player_number === undefined ? '' : String(row.player_number),
        size: row.size ?? '',
        isPair: !!row.is_pair_product,
      });
    }
  }
  return labels;
};
